import { BooleanInput, TabbedForm, useTranslate } from "react-admin";
import {
  FormTab,
  Group,
  GroupItem,
  GroupTitle,
  Toolbar,
  TransactionNotesInput,
  TransactionNotesIsPrivateInput,
  useFieldLabel,
  useSaveMutation,
} from "ra-friendsofbabba";
import { useNotify, usePermissions, useRedirect, useRefresh } from "ra-core";

import AmountGroupInput from "./inputs/AmountGroupInput";
import AttachmentsWarnField from "./fields/AttachmentsWarnField";
import CigCodeInput from "./inputs/CigCodeInput";
import CupCodeInput from "./inputs/CupCodeInput";
import DateGroupInput from "./inputs/DateGroupInput";
import DetermineGroupInput from "./inputs/DetermineGroupInput";
import DocsWarnSign from "./fields/DocsWarnField";
import Input from "../component/input/Input";
import LrInput from "./inputs/LrInput";
import MunicipalityInput from "./inputs/MunicipalityInput";
import OtherInstitutionsInput from "./inputs/OtherInstitutionsInput";
import PermissionableInput from "./inputs/PermissionableInput";
import PermissionableTextContentInput from "./inputs/PermissionableTextContentInput";
import PermissionsField from "./inputs/PermissionsField";
import PositionInput from "./inputs/PositionInput";
import PrintButton from "./buttons/PrintButton";
import ProjectDocsField from "./fields/ProjectDocsField";
import ProjectMediaField from "./fields/ProjectMediaField";
import React from "react";
import TransactionLogsField from "./fields/TransactionLogField";
import TransictoryStateInput from "./inputs/TransictoryStateInput";
import TroInput from "./inputs/TroInput";
import { get } from "lodash";

const Form = ({ ...props }) => {
  const { permissions, loaded } = usePermissions();
  const isAdmin = loaded && permissions && permissions(["admin"]);
  const isRegion = loaded && permissions && permissions(["region", "readonly"]);
  const fieldLabel = useFieldLabel(props);
  const translate = useTranslate();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const notify = useNotify();
  const save = useSaveMutation({
    ...props,
    onSuccess: (response, values) => {
      const prevState = get(values, "transaction.state");
      const nextState = get(response, "data.state");
      const isMoved = prevState !== nextState;
      notify(
        `resources.projects.messages.${values.state}_${
          isMoved ? "moved" : "loop"
        }.save_success`
      );
      if (values.id > 0) {
        refresh();
      } else {
        redirect("edit", props.basePath, response.data.id);
      }
    },
  });

  return (
    <TabbedForm
      {...props}
      warnWhenUnsavedChanges
      initialValues={{
        has_additional_municipalities: false,
        has_other_institutions: false,
        other_institutions: null,
        municipalities: [],
        is_private: false,
      }}
      toolbar={
        !isRegion ? (
          <Toolbar useWorkflow maxButtonsToDisplay={1}>
            <PrintButton />
          </Toolbar>
        ) : (
          <Toolbar useWorkflow />
        )
      }
      save={save}
    >
      <FormTab label={"resources.projects.tabs.general"}>
        <Group fullWidth>
          <GroupItem lg={4} md={5} sm={6}>
            <PermissionableInput
              source="municipality_id"
              component={<MunicipalityInput />}
            />
          </GroupItem>
          {isAdmin && <TransictoryStateInput source="transictory_state" />}
        </Group>
        <Group wrapper>
          <GroupTitle
            title={translate("resources.projects.fields.title")}
            subTitle={translate("resources.projects.fields.title.help_long")}
          />
        </Group>
        <PermissionableTextContentInput
          source="title"
          maxLength={1000}
          multiline
          fullWidth
          useLabel={false}
          useHelp={false}
        />
        <PermissionableInput
          source="position"
          fullWidth
          component={<PositionInput />}
        />
        <PermissionableInput
          source="has_other_institutions"
          fullWidth
          component={<BooleanInput />}
        />
        <PermissionableInput
          source="other_institutions"
          fullWidth
          component={<OtherInstitutionsInput />}
        />
        <Input source="dates" fullWidth component={<DateGroupInput />} />
        <Input
          source="determine"
          fullWidth
          component={<DetermineGroupInput />}
        />

        <PermissionableInput source="cup_code" component={<CupCodeInput />} />
        <PermissionableInput source="cig_code" component={<CigCodeInput />} />
        <Input source="permissions" component={<PermissionsField />} />
        <Group wrapper>
          <GroupTitle title="Logs" />
          <TransactionLogsField admin={isAdmin} fullWidth />
        </Group>
        <TransactionNotesInput
          source="notes"
          helperText="resources.transactions.fields.notes.help"
          fullWidth
        />
        {isAdmin && (
          <TransactionNotesIsPrivateInput
            admin={isAdmin}
            source="is_private"
            helperText="resources.transactions.fields.is_private.help"
            fullWidth
          />
        )}
      </FormTab>
      <FormTab label={"resources.projects.tabs.amounts"}>
        <Input source="amounts" fullWidth component={<AmountGroupInput />} />
      </FormTab>
      <FormTab label={"resources.projects.tabs.tro"}>
        <Input source="tro" fullWidth component={<TroInput />} />
      </FormTab>
      <FormTab label={"resources.projects.tabs.lr"}>
        <Input source="lr" fullWidth component={<LrInput />} />
      </FormTab>
      <FormTab label={"resources.projects.tabs.attachments"}>
        <AttachmentsWarnField />
        <Group wrapper>
          <GroupTitle
            title={fieldLabel("project_media")}
            subTitle={fieldLabel("project_media.help")}
          />
        </Group>
        <Input
          source="project_media"
          fullWidth
          component={<ProjectMediaField />}
        />

        <DocsWarnSign />
        <Group wrapper>
          <GroupTitle
            title={fieldLabel("project_docs")}
            subTitle={fieldLabel("project_docs.help")}
          />
        </Group>
        <Input
          source="project_docs"
          fullWidth
          component={<ProjectDocsField />}
        />
      </FormTab>
    </TabbedForm>
  );
};
export default Form;
